// The identity UI (account domain) and the dashboard hold independent
// sessions: an active identity session doesn't imply the dashboard has one.
// This module owns the redirect contract between them — identity-ui builds
// auto-login URLs, the dashboard login route consumes the params.

export const AUTO_LOGIN_PARAM = "auto_login";
export const RETURN_TO_PARAM = "return_to";
export const PROMPT_PARAM = "prompt";

/**
 * OIDC prompt asking the provider for its registration screen instead of
 * login. Keeps account creation inside the OAuth2 login challenge, so the
 * new account is signed in when the flow completes — no second login.
 */
export const REGISTRATION_PROMPT = "registration";

/**
 * Build the redirect that hands a user over to the dashboard. An identity
 * session doesn't imply a dashboard session, so dashboard-bound targets are
 * routed through the dashboard login with auto sign-in: the OIDC handshake
 * runs immediately instead of stranding the user on the login page. Pass
 * `prompt` to start the flow on a specific provider screen (registration).
 * Non-dashboard targets handle their own authentication and are returned
 * unchanged.
 */
export function buildAutoLoginUrl(
  target: string,
  dashboardUrl: string,
  options?: { prompt?: string }
): string {
  const targetUrl = new URL(target);
  if (targetUrl.origin !== new URL(dashboardUrl).origin) {
    return targetUrl.toString();
  }

  const loginUrl = new URL("/login", targetUrl.origin);
  loginUrl.searchParams.set(AUTO_LOGIN_PARAM, "true");
  loginUrl.searchParams.set(RETURN_TO_PARAM, targetUrl.toString());
  if (options?.prompt) {
    loginUrl.searchParams.set(PROMPT_PARAM, options.prompt);
  }
  return loginUrl.toString();
}

// Auth pages are never valid post-login destinations: a return_to pointing
// back at them would redirect the login route to itself in an endless loop.
const AUTH_PAGE_PATHS = new Set(["/login", "/register"]);
const TRAILING_SLASHES = /\/+$/;

function isAuthPagePath(pathname: string): boolean {
  const normalizedPath = pathname.replace(TRAILING_SLASHES, "") || "/";

  return (
    AUTH_PAGE_PATHS.has(normalizedPath) ||
    [...AUTH_PAGE_PATHS].some((path) => normalizedPath.startsWith(`${path}/`))
  );
}

/**
 * Validate a post-login redirect target coming from a query param. Only
 * same-origin URLs outside the auth pages are honored; anything else falls
 * back to the origin's root so the param can become neither an open redirect
 * nor a redirect loop.
 */
export function getSafeReturnTo(
  returnTo: string | null | undefined,
  origin: string
): string {
  const fallbackUrl = new URL("/", origin).toString();
  if (!returnTo) {
    return fallbackUrl;
  }
  try {
    const parsed = new URL(returnTo, origin);
    if (parsed.origin !== origin || isAuthPagePath(parsed.pathname)) {
      return fallbackUrl;
    }
    return parsed.toString();
  } catch {
    return fallbackUrl;
  }
}

/**
 * Where "Manage my account" lives in the identity UI. It is also Ory's
 * settings UI, so Ory sends settings flows back here with `?flow=`.
 */
export const MANAGE_ACCOUNT_PATH = "/settings";
export const INTENT_PARAM = "intent";
export const DEADLINE_PARAM = "deadline";

/**
 * Why the dashboard sent the operator to the identity UI. `strong-auth`:
 * Arcade refused their sign-in because it didn't use strong authentication.
 * `strong-auth-reminder`: Arcade doesn't require it of them yet, but will
 * from `deadline`.
 */
export const STRONG_AUTH_INTENT = "strong-auth";
export const STRONG_AUTH_REMINDER_INTENT = "strong-auth-reminder";

export type ManageAccountRequest = {
  /** The dashboard page to return to afterwards. */
  returnTo: string;
} & (
  | { intent: typeof STRONG_AUTH_INTENT }
  | {
      intent: typeof STRONG_AUTH_REMINDER_INTENT;
      /** The day strong authentication becomes required (YYYY-MM-DD). */
      deadline: string;
    }
);
export type AccountIntent = ManageAccountRequest["intent"];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Whether `value` is a real calendar day written as YYYY-MM-DD. */
function isIsoDate(value: string | null): value is string {
  if (value === null || !ISO_DATE_RE.test(value)) {
    return false;
  }
  const day = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(day.getTime()) && day.toISOString().slice(0, 10) === value
  );
}

/** The identity UI's "Manage my account" page, opened for an intent when given. */
export function buildManageAccountUrl(
  accountUiUrl: string,
  request?: ManageAccountRequest
): string {
  const url = new URL(MANAGE_ACCOUNT_PATH, accountUiUrl);
  if (request) {
    url.searchParams.set(INTENT_PARAM, request.intent);
    if (request.intent === STRONG_AUTH_REMINDER_INTENT) {
      url.searchParams.set(DEADLINE_PARAM, request.deadline);
    }
    url.searchParams.set(RETURN_TO_PARAM, request.returnTo);
  }
  return url.toString();
}

/**
 * Reads the request back out of a "Manage my account" URL. Ory forgets query
 * params when it starts a settings flow, but keeps the flow's `return_to`, so
 * the identity UI stores this URL there and reads it back on every visit.
 * Anything that isn't the account UI's own page, an unknown intent, or a
 * reminder without a valid deadline is no request; a `return_to` off the dashboard falls back to the dashboard root.
 */
export function readManageAccountRequest(
  url: string | null | undefined,
  { accountUiUrl, dashboardUrl }: { accountUiUrl: string; dashboardUrl: string }
): ManageAccountRequest | null {
  if (!url) {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const page = new URL(MANAGE_ACCOUNT_PATH, accountUiUrl);
  if (parsed.origin !== page.origin || parsed.pathname !== page.pathname) {
    return null;
  }
  const returnTo = getSafeReturnTo(
    parsed.searchParams.get(RETURN_TO_PARAM),
    new URL(dashboardUrl).origin
  );
  const intent = parsed.searchParams.get(INTENT_PARAM);
  if (intent === STRONG_AUTH_INTENT) {
    return { intent, returnTo };
  }
  const deadline = parsed.searchParams.get(DEADLINE_PARAM);
  if (intent === STRONG_AUTH_REMINDER_INTENT && isIsoDate(deadline)) {
    return { intent, deadline, returnTo };
  }
  return null;
}
